// frontend/src/pages/admin/AdminAIAnalyst.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiClient } from '../../api';
import { FaBrain, FaPaperPlane, FaSpinner, FaTrash, FaEraser } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';

function AdminAIAnalyst() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const chatEndRef = useRef(null);

  // --- FETCH HISTORY ON LOAD ---
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch history from the new database-backed endpoint
      const res = await apiClient.get(`/ai-analyst-logs/history`);
      
      // Convert Gemini format back to local format for display
      const formattedHistory = res.map(m => ({
          role: m.role === 'model' ? 'assistant' : 'user',
          content: m.parts[0].text
      }));
      
      if (formattedHistory.length === 0) {
          formattedHistory.push({ role: 'assistant', content: "How can I help with your business analysis?" });
      }
      
      setMessages(formattedHistory);
    } catch (err) {
      console.error("Failed to load chat history:", err);
      setMessages([{ role: 'assistant', content: "Error loading history. Starting new session. How can I help with your business analysis?" }]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isSending) return;
    
    const userMsg = input;
    setInput('');
    
    // 1. Update UI immediately
    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setIsSending(true);

    try {
      // 2. Prepare History for Backend (Send ALL messages)
      // Map 'assistant' -> 'model' and structure as { role, parts: [text] }
      const history = newMessages.map(m => ({
          role: m.role === 'assistant' ? 'model' : 'user',
          parts: [{text: m.content}]
      }));

      // 3. Send Request with History
      const res = await apiClient.post('/ai/executive-chat', { 
          message: userMsg,
          history: history 
      });
      
      setMessages(prev => [...prev, { role: 'assistant', content: res.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: "Error connecting to AI. Please try again." }]);
    } finally { 
      setIsSending(false); 
    }
  };
  
  // --- 1. CLEAR SCREEN ONLY (Local) ---
  const handleClearScreen = () => {
      setMessages([{ role: 'assistant', content: "Screen cleared. (History still exists in database)." }]);
  };

  // --- 2. WIPE DATABASE (Permanent) ---
  const handleWipeDatabase = async () => {
      if (!window.confirm("⚠️ PERMANENT ACTION: This will delete the entire chat history from the database. The AI will lose context of previous conversations. Proceed?")) {
          return;
      }

      setIsClearing(true);
      try {
          await apiClient.delete('/ai-analyst-logs/history');
          setMessages([{ role: 'assistant', content: "Database wiped. Starting fresh session." }]);
      } catch (err) {
          alert("Failed to wipe database.");
      } finally {
          setIsClearing(false);
      }
  };

  return (
    <div className="h-[80vh] flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FaBrain className="text-primary" /> AI Executive Analyst
        </h1>
        
        <div className="flex gap-2">
            {/* Clear Screen Button */}
            <button 
                onClick={handleClearScreen} 
                className="bg-gray-100 text-gray-600 hover:bg-gray-200 px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-bold transition-colors"
                title="Clear Screen (Keeps DB History)"
            >
                <FaEraser /> Clear View
            </button>

            {/* Wipe Database Button */}
            <button 
                onClick={handleWipeDatabase} 
                disabled={isClearing}
                className="bg-red-100 text-red-600 hover:bg-red-200 px-3 py-2 rounded-lg flex items-center gap-2 text-sm font-bold transition-colors"
                title="Permanently Delete History from Database"
            >
                {isClearing ? <FaSpinner className="animate-spin" /> : <FaTrash />} Wipe Memory
            </button>
        </div>
      </div>
      
      <div className="flex-1 bg-white border rounded-2xl shadow-sm overflow-hidden flex flex-col">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {loading ? (
              <div className="text-center py-10"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading History...</div>
          ) : (
              messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`p-4 rounded-xl max-w-[85%] md:max-w-[80%] ${m.role === 'user' ? 'bg-blue-50 text-gray-800' : 'bg-gray-100 text-gray-800'}`}>
                    <div className="text-sm prose prose-sm max-w-none">
                      <ReactMarkdown>{m.content}</ReactMarkdown>
                    </div>
                  </div>
                </div>
              ))
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input Area - Fixed for Mobile */}
        <form onSubmit={handleSend} className="p-3 md:p-4 border-t flex gap-2 items-center bg-gray-50">
          <input 
            value={input} 
            onChange={e => setInput(e.target.value)} 
            className="flex-1 min-w-0 p-3 border rounded-xl outline-none focus:ring-2 focus:ring-primary text-sm" 
            placeholder={isSending ? "Analyzing data..." : "Ask anything about the business..."} 
            disabled={isSending}
          />
          <button 
            type="submit" 
            disabled={isSending} 
            className="bg-primary text-white p-3 md:px-6 rounded-xl font-bold flex-shrink-0 hover:bg-primary-dark transition-colors"
          >
            {isSending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AdminAIAnalyst;